import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { SocialPost } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Checkbox } from '@nucleus-commerce/ui/components/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import { Label } from '@nucleus-commerce/ui/components/label';

import { useRemoveSocialPost } from '../hooks/use-post-mutations';
import { displayPostTitle } from '../schemas/post-state';

export interface RemovePostDialogProps {
  storeId: string;
  /** The post to remove; null keeps the dialog closed. */
  row: SocialPost | null;
  onOpenChange: (open: boolean) => void;
  onRemoved?: (row: SocialPost) => void;
}

function Body({
  storeId,
  row,
  onClose,
  onRemoved,
}: {
  storeId: string;
  row: SocialPost;
  onClose: () => void;
  onRemoved?: (row: SocialPost) => void;
}) {
  const { t } = useTranslation(['social', 'common']);
  const message = useErrorMessage();
  const remove = useRemoveSocialPost(storeId);
  const onNetwork = Boolean(row.externalPostId);
  const [alsoDelete, setAlsoDelete] = useState(onNetwork);
  const [error, setError] = useState<string | null>(null);
  const network = t(`social:${row.provider}.name`);

  const confirm = async () => {
    setError(null);
    try {
      await remove.mutateAsync({ id: row.id, removeFromProvider: onNetwork && alsoDelete });
      onClose();
      onRemoved?.(row);
    } catch (err) {
      setError(message(err));
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {error ? (
        <Alert variant="destructive" role="alert">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}
      {onNetwork ? (
        <div className="flex items-start gap-2">
          <Checkbox
            id="remove-on-network"
            checked={alsoDelete}
            onCheckedChange={(checked) => setAlsoDelete(checked === true)}
          />
          <Label htmlFor="remove-on-network" className="leading-snug">
            {t('social:remove.alsoDelete', { network })}
          </Label>
        </div>
      ) : null}
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onClose} disabled={remove.isPending}>
          {t('common:actions.cancel')}
        </Button>
        <Button
          type="button"
          variant="destructive"
          onClick={() => void confirm()}
          disabled={remove.isPending}
        >
          {remove.isPending ? t('social:remove.working') : t('social:remove.submit')}
        </Button>
      </DialogFooter>
    </div>
  );
}

/** Removes a post in Nucleus and, for a published one, optionally on the network too. */
export function RemovePostDialog({ storeId, row, onOpenChange, onRemoved }: RemovePostDialogProps) {
  const { t } = useTranslation('social');
  return (
    <Dialog open={row !== null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('remove.title')}</DialogTitle>
          <DialogDescription>
            {row
              ? row.externalPostId
                ? t('remove.publishedDescription', { title: displayPostTitle(row) })
                : t('remove.description', { title: displayPostTitle(row) })
              : null}
          </DialogDescription>
        </DialogHeader>
        {row ? (
          <Body
            storeId={storeId}
            row={row}
            onClose={() => onOpenChange(false)}
            onRemoved={onRemoved}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
